import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, HostListener } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TableActionType, TableDataType } from '@app/shared/enums/common.enums';
import { TableAction, TableColumn } from '@app/shared/models/common.model';
import { PPUDropOffModel } from '@app/shared/models/dropoff.model';
import { PPUPaginatedSuccessResponse } from '@app/shared/models/response.model';
import { PACKAGE_STATUS_META, StatusMeta } from '@app/shared/utilities/status.meta';
import { TranslateService } from '@ngx-translate/core';
import { catchError, merge, Observable, of, startWith, switchMap } from 'rxjs';
import { ResponsiveRanges } from '@app/shared/constants';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    standalone: false,
    animations: [
        trigger('fabTriggerRotate', [
            state('closed', style({ transform: 'rotate(0deg)' })),
            state('open', style({ transform: 'rotate(135deg)' })),
            transition('closed <=> open', animate('160ms ease')),
        ])
    ]
})
export class DataTableComponent {
  @Input() dataSource!: PPUPaginatedSuccessResponse<any>;
  @Input() displayedColumns!: Map<string, TableColumn>;
  @Input() withSelection: boolean = false;
  @Input() withPagination: boolean = true;
  @Input() actions: TableAction[] = [];
  @Input() currentPage: number = 0;
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions = [5, 10, 20];
  @Input() selection = new SelectionModel<any>(true, []);
  @Input() statusClickable: boolean = true;
  @Input() clickableColumns: Map<string, EventEmitter<any>> = new Map();
  @Input() actionFilterFn?: (action: TableAction, row: any) => boolean;
  @Input() showTooltipFn?: (columnName: string, row: any) => boolean;
  @Input() statusMeta: Record<number, StatusMeta> = PACKAGE_STATUS_META;
  @Input() actionsAtEnd: boolean = false;
  @Output() actionTriggered: EventEmitter<{ action: TableActionType; row: any }> = new EventEmitter();
  @Output() pageChange = new EventEmitter<any>();
  @Output() statusClick: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  tableData!: Observable<PPUDropOffModel[]>;
  columns: string[] | undefined;
  resultsLength: number | undefined;
  isMobile: boolean = false;

  constructor(private translate: TranslateService) {}

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.handleScreenSizeChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && this.dataSource) {
      this.resultsLength = this.dataSource.resultCount ?? 0;
      this.updateTable();
      this.selection.clear();
    }
  }

  ngOnInit() {
    this.columns = this.generateColumns();
    this.handleScreenSizeChange();
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe(() => {
      this.updateTable();
    });
    // Set initial label
    this.updatePaginatorLabel();
    
    // Update when language changes
    this.translate.onLangChange.subscribe(() => {
      this.updatePaginatorLabel();
    });
  }

  private updatePaginatorLabel(): void {
    this.paginator._intl.itemsPerPageLabel = this.translate.instant('App.Dropoff.Table.ItemsPerPage');
    this.paginator._intl.changes.next();
  }

  generateColumns() {
    const baseColumns: string[] = [];

    // Single action vs multiple
    const actionLabel = this.actions.length === 1 ? 'App.Common.Table.Action' : 'App.Common.Table.Actions';

    if (this.actions.length && !this.displayedColumns.has('actions')) {
      this.displayedColumns.set('actions', {
        label: actionLabel,
        format: TableDataType.Text,
      });
    }

    if (this.withSelection) baseColumns.push('select');
    if (this.actions.length && !this.actionsAtEnd) baseColumns.push('actions');

    // Add the rest of the columns, excluding 'select' and 'actions'
    for (const column of this.displayedColumns.keys()) {
      if (column !== 'select' && column !== 'actions') {
        baseColumns.push(column);
      }
    }

    // Add actions at end if specified
    if (this.actions.length && this.actionsAtEnd) baseColumns.push('actions');

    return baseColumns;
  }

  private updateTable(): void {
    this.tableData = merge(this.paginator.page).pipe(
      startWith({}),
      switchMap(() => {
        return of(this.dataSource.results ?? [this.dataSource.result]);
      }),
      catchError(() => {
        return of([]);
      })
    );

    this.resultsLength = this.dataSource?.resultCount ?? 0;
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.results.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.results);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  onRowClick(row: any): void {
    if (this.withSelection) {
      this.selection.toggle(row);
    }
  }

  onColumnClick(column: string, row: any){
    const eventClick = this.clickableColumns.get(column);
    if(eventClick){
      eventClick.emit(row);
    }
  }

  onActionClick(event: any, actionName: TableActionType, row: any) {
    event.stopPropagation();
    this.actionTriggered.emit({ action: actionName, row });
  }

  onPageChange(e: PageEvent) {
    this.selection.clear();
    this.pageChange.emit(e);
  }

  onStatusClick(row: any): void {
    this.statusClick.emit(row.packageId); 
  }

  // Get class for each status
  getStatusClass(id: number): string {
    return this.statusMeta[id]?.css ?? 'status-grey';
  }

  // Check if the column is a status column based on metadata
  isStatusColumn(columnName: string): boolean {
    const column = this.displayedColumns.get(columnName);
    return column?.isStatusColumn || false;
  }

  // Get mapped status
  getMappedStatus(id: number): string {
    return this.statusMeta[id]?.key ?? 'App.Status.unknown';
  }

  hasTooltip(columnName: string, row: any): boolean {
    if (this.showTooltipFn && this.showTooltipFn!(columnName, row)) {
      const column = this.displayedColumns.get(columnName);
      return !!column?.tooltip && column?.tooltip.length > 0;
    }
    return false;
  }

  getRowActions(actions: TableAction[], row: any): TableAction[] {
    if (this.actionFilterFn) {
      return actions.filter(action => this.actionFilterFn!(action, row));
    }
    return actions;
  }

  private handleScreenSizeChange() {
    this.isMobile = window.innerWidth < ResponsiveRanges.mobile;
  }  

  getMobileColumns(): string[] {
    if (!this.columns) return [];
    // Filter out 'select' and 'actions' columns for mobile content
    const contentColumns = this.columns.filter(col => col !== 'select' && col !== 'actions');
    return contentColumns;
  }
}
